import { Formik, Form } from "formik";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Button,
  Confirm,
  Divider,
  Grid,
  Header,
  Icon,
  Segment,
  Popup,
} from "semantic-ui-react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  deletePartyInDb,
  deleteSharedEmailClientInDb,
  updateTransactionInDb,
  updateTransClientInDb,
  updatePartyInDb,
  updateDocInDb,
} from "../../../app/firestore/firestoreService";
import { closeModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import MySelectInput from "../../../app/common/form/MySelectInput";
import {
  createClientRoleOptions,
  createRestrictedEmails,
  partyIsBuyerOrSeller,
  partyIsSeller,
} from "../../../app/common/util/util";
import FormParty from "../../../app/common/form/FormParty";
import FormAddress from "../../../app/common/form/FormAddress";
import MyCheckbox from "../../../app/common/form/MyCheckbox";
import MyTextInput from "../../../app/common/form/MyTextInput";
import { useMediaQuery } from "react-responsive";
import { useFormikContext } from "formik";
import { useEffect } from "react";

// Helper function to create signer name format
const createSignerName = (firstName, middleName, lastName, authorityTitle) => {
  if (!firstName && !lastName) return "";

  let name = "";
  if (firstName) name += firstName;
  if (middleName) name += (name ? " " : "") + middleName;
  if (lastName) name += (name ? " " : "") + lastName;
  if (authorityTitle) name += ", " + authorityTitle;

  return name;
};

// Helper function to update primary party's sharedEmailClients when secondary/tertiary client shares email
async function updatePrimaryPartySharedEmailClients(
  clientValues,
  savedClientId,
  allParties
) {
  console.log(
    "updatePrimaryPartySharedEmailClients called with: ",
    clientValues,
    savedClientId
  );
  const role = clientValues.role;
  const isSecondaryOrTertiary =
    role === "Buyer 2" ||
    role === "Buyer 3" ||
    role === "Seller 2" ||
    role === "Seller 3";

  if (!isSecondaryOrTertiary) {
    console.log("Not secondary/tertiary, returning");
    return;
  }

  // Determine the primary role
  const primaryRole = role.startsWith("Buyer") ? "Buyer" : "Seller";
  const primaryParty = allParties.find((p) => p.role === primaryRole);

  if (!primaryParty) {
    console.log("Primary party not found for role:", primaryRole);
    return;
  }

  console.log("Found primary party:", primaryParty);

  // Get current sharedEmailClients array or initialize empty
  let sharedEmailClients = primaryParty.sharedEmailClients || [];
  console.log("Current sharedEmailClients:", sharedEmailClients);

  // Remove any existing entry for this party's role
  sharedEmailClients = sharedEmailClients.filter((item) => item.role !== role);

  // If checkbox is checked, add the new entry and copy primary's email to secondary/tertiary
  if (clientValues.sharesEmailWithPrimary) {
    console.log("Checkbox is checked, adding to sharedEmailClients");
    sharedEmailClients.push({
      role: role,
      odPartyId: savedClientId,
    });

    // Also update the secondary/tertiary party's email to match the primary party's email
    if (primaryParty.email) {
      console.log(
        `Copying primary party's email (${primaryParty.email}) to secondary/tertiary party`
      );
      // Update the party directly by ID
      await updatePartyInDb({ id: savedClientId, role: role }, { email: primaryParty.email });
      console.log("Secondary/tertiary party email updated successfully");
    }
  } else {
    console.log(
      "Checkbox is NOT checked, sharesEmailWithPrimary:",
      clientValues.sharesEmailWithPrimary
    );
    // Don't modify the email here - the form submission already handles
    // updating the email to whatever the user entered. We only need to
    // update the sharedEmailClients array (which was already done above
    // by filtering out this party's role).
  }

  console.log("Updated sharedEmailClients:", sharedEmailClients);
  // Update the primary party
  await updatePartyInDb(primaryParty, { sharedEmailClients });
  console.log("Primary party updated successfully");
}

export default function TransactionClientEditForm({ client, transaction }) {
  const dispatch = useDispatch();
  const { allParties } = useSelector((state) => state.transaction);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { docsTransActive, docsTransArchived, docsTransShared } = useSelector(
    (state) => state.doc
  );
  const { tasksTransWithDateAll } = useSelector((state) => state.task);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  const clientRoleOptions = createClientRoleOptions(client, transaction);

  const originalClientEmail = client.email;

  const restrictedEmails = createRestrictedEmails(
    currentUserProfile,
    transaction,
    allParties,
    client
  );

  let emailIsNotEditable = true;
  if (!client.email || !transaction.sharingWith?.includes(client.email)) {
    emailIsNotEditable = false;
  }

  let initialValues = {
    ...client,
    authorityTitle: client.authorityTitle || "",
    signerName: client.signerName || "",
    hasPoa: client.hasPoa || false,
    poaFullName: client.poaFullName || "",
    poaTitle: client.poaTitle || "",
    poaDisplayOption: client.poaDisplayOption || "client_by_poa",
    poaCustomDisplay: client.poaCustomDisplay || "",
  };

  const [isTrustField, setIsTrustField] = useState(
    initialValues.isTrust ? true : false
  );
  const [hasPoaField, setHasPoaField] = useState(
    initialValues.hasPoa ? true : false
  );
  const [selectedRole, setSelectedRole] = useState(initialValues.role);

  // Watcher component for role changes
  function RoleWatcher({ role }) {
    React.useEffect(() => {
      setSelectedRole(role);
    }, [role]);
    return null;
  }

  // Watcher component for signer name updates
  function SignerNameWatcher() {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
      if (values.isTrust) {
        const signerName = createSignerName(
          values.firstName,
          values.middleName,
          values.lastName,
          values.authorityTitle || ""
        );
        setFieldValue("signerName", signerName);
      } else {
        setFieldValue("signerName", "");
      }
    }, [
      values.firstName,
      values.middleName,
      values.lastName,
      values.authorityTitle,
      values.isTrust,
      setFieldValue,
    ]);

    return null;
  }

  const validationSchema = Yup.object({
    lastName: Yup.string().required("You must provide a last name"),
    role: Yup.string().required("You must provide a role"),
    email: Yup.string()
      .email("Must be a valid email")
      .test(
        "email-restriction",
        "Can't use same email for different parties",
        function (value) {
          if (!value) return true; // Allow empty email

          const { role, sharesEmailWithPrimary } = this.parent;

          // If this is a secondary/tertiary client sharing email with primary, allow it
          const isSecondaryOrTertiary =
            role === "Buyer 2" ||
            role === "Buyer 3" ||
            role === "Seller 2" ||
            role === "Seller 3";

          if (isSecondaryOrTertiary && sharesEmailWithPrimary) {
            // Find the primary party
            const primaryRole = role.startsWith("Buyer") ? "Buyer" : "Seller";
            const primaryParty = allParties?.find(
              (p) => p.role === primaryRole
            );

            // Allow if email matches primary party's email
            if (primaryParty && value === primaryParty.email) {
              return true;
            }
          }

          // Otherwise, check against restricted emails
          const restrictedEmails = createRestrictedEmails(
            currentUserProfile,
            transaction,
            allParties,
            client // Pass existing party to exclude its own email
          );

          return !restrictedEmails.includes(value);
        }
      ),
    poaFullName: Yup.string().when("hasPoa", {
      is: true,
      then: (schema) => schema.required("POA full name is required"),
    }),
    poaTitle: Yup.string().when("hasPoa", {
      is: true,
      then: (schema) => schema.required("POA title is required"),
    }),
    poaCustomDisplay: Yup.string().when(["hasPoa", "poaDisplayOption"], {
      is: (hasPoa, poaDisplayOption) => hasPoa && poaDisplayOption === "custom",
      then: (schema) => schema.required("Custom display text is required"),
    }),
  });

  const poaDisplayOptions = [
    {
      key: "client_by_poa",
      value: "client_by_poa",
      text: "Client's name, by POA's name, as Attorney-in-Fact",
    },
    {
      key: "poa_for_client",
      value: "poa_for_client",
      text: "POA's name, as Attorney-in-Fact for Client's name",
    },
    {
      key: "custom",
      value: "custom",
      text: "Custom display (enter below)",
    },
  ];

  async function handleDelete() {
    let docs = [];
    if (docsTransActive?.docs) {
      docs = docs.concat(docsTransActive?.docs);
    }
    if (docsTransArchived?.docs) {
      docs = docs.concat(docsTransArchived?.docs);
    }
    if (docsTransShared?.docs) {
      docs = docs.concat(docsTransShared?.docs);
    }
    let tasks = tasksTransWithDateAll;
    try {
      // Helper function to clean up shared email client references
      const cleanupSharedEmailClient = async (deletedClient, alsoDeleteThird = false) => {
        const primaryRole = deletedClient.role.startsWith("Buyer") ? "Buyer" : "Seller";
        const primaryParty = allParties.find((p) => p.role === primaryRole);
        const rolesToClean = [deletedClient.role];

        // If also deleting third client, add their role to cleanup
        if (alsoDeleteThird && transaction.clientThird?.role) {
          rolesToClean.push(transaction.clientThird.role);
        }

        // (1) Update transaction.client.sharedEmailClients - remove deleted client(s)
        let updatedClientSharedEmailClients = transaction.client?.sharedEmailClients || [];
        updatedClientSharedEmailClients = updatedClientSharedEmailClients.filter(
          (item) => !rolesToClean.includes(item.role) && item.odPartyId !== deletedClient.id
        );
        if (alsoDeleteThird && transaction.clientThird?.id) {
          updatedClientSharedEmailClients = updatedClientSharedEmailClients.filter(
            (item) => item.odPartyId !== transaction.clientThird.id
          );
        }

        // (2) Build transaction update fields - clear sharingWithRole for deleted client(s)
        const transactionUpdateFields = {
          "client.sharedEmailClients": updatedClientSharedEmailClients,
        };

        // Clear sharingWithRole for each deleted role
        rolesToClean.forEach((role) => {
          const roleCamel = role.replace(" ", "").toLowerCase();
          transactionUpdateFields[`sharingWithRole.${roleCamel}`] = "";
        });

        // (3) Update primary party's sharedEmailClients in parties collection
        if (primaryParty) {
          let primarySharedEmailClients = primaryParty.sharedEmailClients || [];
          primarySharedEmailClients = primarySharedEmailClients.filter(
            (item) => !rolesToClean.includes(item.role) && item.odPartyId !== deletedClient.id
          );
          if (alsoDeleteThird && transaction.clientThird?.id) {
            primarySharedEmailClients = primarySharedEmailClients.filter(
              (item) => item.odPartyId !== transaction.clientThird.id
            );
          }
          await updatePartyInDb(primaryParty, { sharedEmailClients: primarySharedEmailClients });
        }

        // (4) & (5) For each document, clean signerListInProgress and ensure primary email in sharingWith
        for (const document of docs) {
          const docUpdateFields = {};

          // (4) Remove deleted client role(s) from signerListInProgress
          if (document.signerListInProgress && Array.isArray(document.signerListInProgress)) {
            const filteredSignerList = document.signerListInProgress.filter(
              (signer) => !rolesToClean.includes(signer.role)
            );
            if (filteredSignerList.length !== document.signerListInProgress.length) {
              docUpdateFields.signerListInProgress = filteredSignerList;
            }
          }

          // (5) Ensure primary client's email is still in sharingWith if doc was shared with them
          if (primaryParty?.email && document.sharingWithRole) {
            const primaryRoleCamel = primaryRole.toLowerCase();
            if (document.sharingWithRole[primaryRoleCamel] === primaryParty.email) {
              // Primary is shared, ensure email is in sharingWith array
              if (document.sharingWith && !document.sharingWith.includes(primaryParty.email)) {
                docUpdateFields.sharingWith = [...document.sharingWith, primaryParty.email];
              }
            }
          }

          // Update document if there are changes
          if (Object.keys(docUpdateFields).length > 0) {
            await updateDocInDb(document.id, docUpdateFields);
          }
        }

        return transactionUpdateFields;
      };

      if (client.role === "Buyer 2" || client.role === "Seller 2") {
        // Check if secondary client shares email with primary
        const secondarySharesEmail = client.sharesEmailWithPrimary ||
          (transaction.clientSecondary?.sharesEmailWithPrimary);

        const alsoDeleteThird = transaction.clientThird?.lastName?.length > 0;
        const thirdSharesEmail = transaction.clientThird?.sharesEmailWithPrimary;

        // Clean up shared email client references if applicable
        if (secondarySharesEmail || thirdSharesEmail) {
          const additionalTransFields = await cleanupSharedEmailClient(client, alsoDeleteThird);

          // Use deleteSharedEmailClientInDb for clients who share email with primary
          // This only clears sharingWithRole but does NOT remove email from sharingWith
          if (secondarySharesEmail) {
            await deleteSharedEmailClientInDb(client, transaction, docs, tasks);
          } else {
            deletePartyInDb(client, transaction, docs, tasks);
          }

          if (alsoDeleteThird) {
            if (thirdSharesEmail) {
              await deleteSharedEmailClientInDb(transaction.clientThird, transaction, docs, tasks);
            } else {
              deletePartyInDb(transaction.clientThird, transaction, docs, tasks);
            }
          }

          updateTransactionInDb(transaction.id, {
            ...additionalTransFields,
            clientSecondary: {},
            clientSecondaryExists: false,
            clientThird: {},
            clientThirdExists: false,
          });
        } else {
          deletePartyInDb(client, transaction, docs, tasks);
          if (alsoDeleteThird) {
            deletePartyInDb(transaction.clientThird, transaction, docs, tasks);
          }
          updateTransactionInDb(transaction.id, {
            clientSecondary: {},
            clientSecondaryExists: false,
            clientThird: {},
            clientThirdExists: false,
          });
        }
      } else if (client.role === "Buyer 3" || client.role === "Seller 3") {
        // Check if third client shares email with primary
        const sharesEmail = client.sharesEmailWithPrimary ||
          (transaction.clientThird?.sharesEmailWithPrimary);

        if (sharesEmail) {
          const additionalTransFields = await cleanupSharedEmailClient(client, false);

          // Use deleteSharedEmailClientInDb to only clear sharingWithRole but NOT remove email
          await deleteSharedEmailClientInDb(client, transaction, docs, tasks);
          updateTransactionInDb(transaction.id, {
            ...additionalTransFields,
            clientThird: {},
            clientThirdExists: false,
          });
        } else {
          deletePartyInDb(client, transaction, docs, tasks);
          updateTransactionInDb(transaction.id, {
            clientThird: {},
            clientThirdExists: false,
          });
        }
      }

      toast.success("client successfully deleted");
      dispatch(
        closeModal({
          modalType: "TransactionClientEditForm",
        })
      );
    } catch (error) {
      toast.error(error.message);
    }
  }

  return (
    <ModalWrapper>
      <Segment clearing>
        <div className="medium horizontal margin small top margin">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                console.log("Start client edit form submit");
                const docsInTransaction =
                  values.email !== originalClientEmail
                    ? docsTransActive?.docs
                        ?.concat(docsTransArchived?.docs)
                        .concat(docsTransShared?.docs)
                    : [];

                const docsNeedSharingUpdated = docsInTransaction
                  ? docsInTransaction?.filter((doc) =>
                      doc.sharingWith?.includes(originalClientEmail)
                    )
                  : [];

                // Check if this is a secondary/tertiary client who was sharing email with primary
                // and is now unchecking the checkbox. In this case, we need to keep the primary's
                // email in sharingWith arrays while adding the secondary's new email.
                const isSecondaryOrTertiary =
                  values.role === "Buyer 2" ||
                  values.role === "Buyer 3" ||
                  values.role === "Seller 2" ||
                  values.role === "Seller 3";
                const wasSharedEmail = client.sharesEmailWithPrimary === true;
                const isNowUnshared = values.sharesEmailWithPrimary === false;
                const keepOriginalEmailInSharing =
                  isSecondaryOrTertiary && wasSharedEmail && isNowUnshared;
console.log("ON SAVE CLIENT:: docsNeedSharingUpdated", docsNeedSharingUpdated);
console.log("ON SAVE CLIENT:: keepOriginalEmailInSharing", keepOriginalEmailInSharing);
                await updateTransClientInDb(
                  transaction,
                  values,
                  originalClientEmail,
                  docsNeedSharingUpdated,
                  tasksTransWithDateAll,
                  keepOriginalEmailInSharing
                );

                // Update primary party's sharedEmailClients if applicable
                await updatePrimaryPartySharedEmailClients(
                  values,
                  values.id,
                  allParties
                );
                console.log("Client edit form submit completed");

                setSubmitting(false);
                toast.success("Client successfully updated");
                dispatch(
                  closeModal({
                    modalType: "TransactionClientEditForm",
                  })
                );
              } catch (error) {
                toast.error(error.message);
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, dirty, isValid, values }) => (
              <Form className="ui form">
                <RoleWatcher role={values.role} />
                <SignerNameWatcher />
                <Header size="large" color="blue">
                  Edit Client
                </Header>
                <Divider />
                <Grid>
                  <Grid.Column mobile={16} computer={5}>
                    <MySelectInput
                      name="role"
                      placeholder="Role"
                      options={clientRoleOptions}
                    />
                  </Grid.Column>
                </Grid>
                <Divider />
                <FormParty
                  edit={emailIsNotEditable}
                  role={values.role}
                  newPerson={false}
                />

                {partyIsBuyerOrSeller(client.role) && (
                  <>
                    <br />
                    <MyCheckbox
                      name="isTrust"
                      label="This client is acting on behalf of a company, trust, or other entity"
                      onClick={() => setIsTrustField(!isTrustField)}
                    />
                    {isTrustField && (
                      <>
                        <MyTextInput
                          name="entityName"
                          label="Company, trust, or entity name"
                        />
                        <MyTextInput
                          name="authorityTitle"
                          label="Authority title (e.g., Manager, Trustee, President, Member)"
                          placeholder="Manager"
                        />
                        <MyTextInput
                          name="signerName"
                          label="Signer name (auto-generated)"
                          readOnly
                          style={{ backgroundColor: "#f8f8f8" }}
                        />
                      </>
                    )}
                  </>
                )}

                {/* POA Section - Only show for Seller roles */}
                {partyIsSeller(selectedRole) && (
                  <>
                    <br />
                    <MyCheckbox
                      name="hasPoa"
                      label="This client will be represented by a Power of Attorney (POA)"
                      onClick={() => setHasPoaField(!hasPoaField)}
                    />
                    {hasPoaField && (
                      <>
                        <Grid>
                          <Grid.Row>
                                                      <Grid.Column mobile={16} computer={16}>
                                                        <p>
                                                          The email above should be for the power of attorney individual.
                                                          <br/><i>If you want to share documents to your client who the POA represents, add your client's email to "Other Parties" using the "Other" role and click their button to share documents with them.</i>
                                                        </p>
                                                      </Grid.Column>
                                                      </Grid.Row>
                                                    <Grid.Row></Grid.Row>
                          <Grid.Row>
                            <Grid.Column mobile={16} computer={8}>
                              <MyTextInput
                                name="poaFullName"
                                label="POA's Full Name"
                                placeholder="Enter POA's full name"
                              />
                            </Grid.Column>
                            <Grid.Column mobile={16} computer={8}>
                              <MyTextInput
                                name="poaTitle"
                                label="POA's Title"
                                placeholder="Enter POA's title"
                              />
                            </Grid.Column>
                          </Grid.Row>
                          <Grid.Row>
                            <Grid.Column width={16}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <label style={{ marginRight: "8px" }}>
                                  Signature Display Option
                                </label>
                                <Popup
                                  trigger={
                                    <Icon name="info circle" color="blue" />
                                  }
                                  content={
                                    <div>
                                      <p>
                                        <strong>
                                          Examples of how signatures will
                                          display:
                                        </strong>
                                      </p>
                                      <p>
                                        <strong>Option A:</strong> John Q.
                                        Principal, by Jane R. Agent, as
                                        Attorney-in-Fact
                                      </p>
                                      <p>
                                        <strong>Option B:</strong> Jane R.
                                        Agent, as Attorney-in-Fact for John Q.
                                        Principal
                                      </p>
                                      <p>
                                        <strong>Custom:</strong> You can enter
                                        your own display format
                                      </p>
                                      <br />
                                      <p>
                                        <strong>Note:</strong> The email for
                                        this client should be the POA's email
                                        address.
                                      </p>
                                      <p>
                                        <strong>
                                          Initials will display as:
                                        </strong>{" "}
                                        Client's initials by POA's initials
                                        (AIF)
                                      </p>
                                      <p>
                                        <strong>Example:</strong> JQP by JRA
                                        (AIF)
                                      </p>
                                    </div>
                                  }
                                  wide="very"
                                  position="top center"
                                />
                              </div>
                              <MySelectInput
                                name="poaDisplayOption"
                                options={poaDisplayOptions}
                                placeholder="Select display option"
                              />
                            </Grid.Column>
                          </Grid.Row>
                          {values.poaDisplayOption === "custom" && (
                            <Grid.Row>
                              <Grid.Column width={16}>
                                <MyTextInput
                                  name="poaCustomDisplay"
                                  label="Custom Signature Display"
                                  placeholder="Enter how you want the signature to display"
                                />
                              </Grid.Column>
                            </Grid.Row>
                          )}
                        </Grid>
                      </>
                    )}
                  </>
                )}

                <Divider />
                <FormAddress />
                <Divider className="medium top margin" />
                <Button
                  loading={isSubmitting}
                  disabled={!isValid || !dirty || isSubmitting}
                  type="submit"
                  floated={isMobile ? null : "right"}
                  primary
                  content="Submit"
                  className={isMobile ? "fluid medium bottom margin" : null}
                />
                <Button
                  disabled={isSubmitting}
                  onClick={() =>
                    dispatch(
                      closeModal({
                        modalType: "TransactionClientEditForm",
                      })
                    )
                  }
                  to="#"
                  type="button"
                  floated={isMobile ? null : "right"}
                  content="Cancel"
                  className={isMobile ? "fluid medium bottom margin" : null}
                />
                {!(client.role === "Buyer" || client.role === "Seller") &&
                  !isMobile && (
                    <Icon
                      name="trash"
                      link
                      onClick={() => setConfirmOpen(true)}
                    />
                  )}
                {!(client.role === "Buyer" || client.role === "Seller") &&
                  isMobile && (
                    <Button
                      type="button"
                      basic
                      color="red"
                      content="Delete"
                      fluid
                      onClick={() => setConfirmOpen(true)}
                    />
                  )}
              </Form>
            )}
          </Formik>
        </div>
      </Segment>
      <Confirm
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => handleDelete()}
      ></Confirm>
    </ModalWrapper>
  );
}
